// web-v2/src/lib/quoteAxes.js
//
// OPTIONS ARE THE PRODUCT OF AXES, NOT A LIST THE BROKER MAINTAINS.
//
// David's layout, verbatim (2026-08-07):
//
//   "stack in column A Per Occurrence & Per Passenger and then beside it in
//    Column B is premium; underneath in A is Medical Payments, beside it in B is
//    premium. if there's a + it's for the Liabs section and it replicates
//    it/squeezes it in between the original and med pay; same for med pay:
//    another + and it appends the next option to the bottom there."
//
// So the broker edits TWO LISTS: N liability limits and M medical-payments
// limits. What gets STORED is still `quote_options`, one row per combination,
// because bind, present, the CRM mirror and the client card all read that shape
// and none of them should learn a new one. CHANGE THE EDITOR, NOT THE STORE.
//
// WHY THE PRODUCT AND NOT A NEW TABLE. The Tower's independent toggles are safe
// under exactly one condition:
//
//     Independent toggles are permitted exactly when every combination exists
//     as a stored option.
//
// `public/assets/tower-option-grouping.js` CHECKS that today (analyseGroup()
// .complete) and falls back to one-button-per-option when it fails. Generated
// corners satisfy it BY CONSTRUCTION, so the check stops being the only thing
// standing between a client and a price nobody quoted and becomes belt and
// braces.
//
// ── THIS MODULE IS THE ONE DERIVATION ─────────────────────────────────────
// The server enforces the grid (services/quoting-engine.service.js requires this
// file) and the wizard renders it. One Fact One Derivation: the two used to be
// the shape of bug this project keeps finding, so there is deliberately no
// second copy of `is this set a grid` or `what does this corner cost`.
//
// Pure ESM, React-free, no imports, so node can require() it (the same way
// tests/state-tax-suggestion.test.js already requires web-v2/src/lib/stateTax.js
// and passes the Render pre-deploy gate).

// ── NUMBERS ───────────────────────────────────────────────────────────────
// Supabase hands numerics back as strings ("1000000.00"); the form hands them
// back as typed strings ("1,000,000"). Both normalise here so a limit read off
// the row and the same limit typed into the box produce the SAME key.
export function axisNum(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === '') return null;
  const n = Number(s.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// ── COORDINATES ───────────────────────────────────────────────────────────
// A corner is identified by WHERE IT SITS on the two axes, never by its
// option_number and never by its position in the array. option_number repeats
// (quote f8edf033 carries 1, 2, 2, 1) and the array comes back unordered.
export function liabilityCoord(o) {
  return {
    per_occurrence: axisNum(o?.liability_per_occurrence ?? o?.liability_csl),
    per_passenger: axisNum(o?.liability_per_passenger ?? o?.per_passenger),
    // NULL is not false. A row that never said whether the limit is smooth is a
    // different corner-coordinate from one that said split, and flattening the
    // two here would merge two legs the carrier priced separately.
    smooth: o?.liability_smooth === null || o?.liability_smooth === undefined
      ? null : o.liability_smooth === true,
  };
}

export function medicalCoord(o) {
  return { per_person: axisNum(o?.medical_payments ?? o?.medical_per_person) };
}

// A SMOOTH LIMIT HAS NO SUBLIMIT (2026-09-09). The wizard hides the
// per-passenger field on a smooth leg but keeps its value, so a leg flipped
// split -> smooth arrives with a sublimit riding along. The engine already
// nulls it on the stored corner; the key has to agree, or the leg and its own
// corner are two keys and the moved-check fires on every save.
export function liabilityKey(c) {
  const pax = c?.smooth === true ? '' : (c?.per_passenger ?? '');
  return `L:${c?.per_occurrence ?? ''}/${pax}/${c?.smooth ?? ''}`;
}

export function medicalKey(c) {
  return `M:${c?.per_person ?? ''}`;
}

// A leg with no limit at all is the "this quote does not break this out" leg. It
// contributes nothing to a corner's price, which is what Allianz's two options
// (no medical payments recorded, totals = hull + liability exactly) already say.
export function liabilityLegIsBlank(l) {
  return axisNum(l?.per_occurrence) == null && axisNum(l?.per_passenger) == null;
}
export function medicalLegIsBlank(m) {
  return axisNum(m?.per_person) == null;
}

// ── WHAT EVERY CORNER OF ONE GRID MUST AGREE ON ───────────────────────────
// If two options disagree here they are not two points on one grid, they are two
// different offers, and this module must say so rather than force them together.
//
// `aircraft_coverage` is DELIBERATELY ABSENT. syncNestedAircraftPremium copies
// each option's total into its single aircraft row, so the nested block differs
// between corners BY DESIGN; including it would mark every real grid as not one.
// Milam's Mach 2 corners carry nested premiums 8932 / 9287 / 8992 / 9227.
export const SHARED_CORNER_FIELDS = [
  'hull_premium',
  'hull_value',
  'deductible_in_motion',
  'deductible_not_in_motion',
  'coverage_type',
  'term_months',
];

function sharedValue(o, f) {
  if (f === 'deductible_in_motion' || f === 'deductible_not_in_motion' || f === 'coverage_type') {
    // Deductibles are TEXT and legitimately hold "10%" as well as "27500".
    const s = String(o?.[f] ?? '').trim();
    return s === '' ? '' : s;
  }
  const n = axisNum(o?.[f]);
  return n == null ? '' : String(n);
}

const SHARED_LABEL = {
  hull_premium: 'the hull premium',
  hull_value: 'the hull value',
  deductible_in_motion: 'the in-motion deductible',
  deductible_not_in_motion: 'the not-in-motion deductible',
  coverage_type: 'the coverage type',
  term_months: 'the term',
};

// ── THE PRICE OF A CORNER ─────────────────────────────────────────────────
// hull + the chosen liability leg + the chosen medical leg. That is the BASE
// premium as this codebase already defines it (quoteWizardModel.premiumDelta:
// TRIA is an election added on top at election time and the admin fee is not
// premium at all), and it reproduces Milam's four live Mach 2 totals exactly:
//
//   7900 + 972  + 60  = 8932     7900 + 1267 + 120 = 9287
//   7900 + 972  + 120 = 8992     7900 + 1267 + 60  = 9227
//
// NULL WHEN IT CANNOT BE DERIVED, never a guess and never a partial sum. A leg
// that carries a limit and no premium cannot price a combination, and a residual
// off a short sum is a measurement of what the sum forgot.
export function cornerTotal({ hull_premium, liability, medical }) {
  const hull = axisNum(hull_premium);
  if (hull == null) return null;
  const legPart = (leg, blank) => {
    if (!leg || blank(leg)) return 0;
    return axisNum(leg.premium);
  };
  const l = legPart(liability, liabilityLegIsBlank);
  const m = legPart(medical, medicalLegIsBlank);
  if (l == null || m == null) return null;
  return hull + l + m;
}

// Everything standing between the axes as they are and a set of corners that can
// all be priced. Returns [] when the grid can be generated.
//
// A SINGLE CORNER IS EXEMPT, and that exemption is the "line iteming isn't
// necessary until there are choices" ruling in code: a carrier that quoted one
// package states a total and nothing else, and that is a complete answer. The
// legs only have to carry prices once there is a second limit to choose between.
export function pricingBlockers({ hull_premium, liability, medical, tria_status }) {
  const legs = { liability: liability || [], medical: medical || [] };
  if (legs.liability.length * legs.medical.length <= 1) return [];
  const out = [];
  if (axisNum(hull_premium) == null) {
    out.push('the hull premium, which every combination is built on (enter 0 if there is no hull coverage)');
  }
  for (const l of legs.liability) {
    if (!liabilityLegIsBlank(l) && axisNum(l.premium) == null) {
      out.push(`what the ${describeLiability(l)} liability limit costs`);
    }
    // TRIA IS AN ELECTION AT A PRICE, AND THE PRICE RIDES THE LIABILITY LEG.
    // Said here rather than left to the insert's assertTriaAnswerShape, which
    // would refuse the save with a database-shaped sentence after the broker had
    // already typed a second limit.
    if (tria_status === 'optional' && !liabilityLegIsBlank(l) && axisNum(l.tria_premium) == null) {
      out.push(`the TRIA amount for the ${describeLiability(l)} limit (TRIA is optional on this quote)`);
    }
  }
  for (const m of legs.medical) {
    if (!medicalLegIsBlank(m) && axisNum(m.premium) == null) {
      out.push(`what the ${describeMedical(m)} medical payments limit costs`);
    }
  }
  return out;
}

function money(v) {
  const n = axisNum(v);
  if (n == null) return '';
  if (n >= 1e6 && n % 1e5 === 0) return `$${n / 1e6}M`;
  if (n >= 1e3 && n % 100 === 0) return `$${n / 1e3}K`;
  return `$${n.toLocaleString('en-US')}`;
}

// Smooth is read FIRST. This read the pair first, so a smooth leg still
// carrying its hidden sublimit was named "$2M/$250K", and that name went on
// the stored corner and the client card beside a lit "$2M CSL" toggle (Starr,
// 2026-09-09). CSL is claimed only from an explicit true; a null with a
// sublimit still names the pair, which is what the two numbers say.
export function describeLiability(l) {
  const occ = money(l?.per_occurrence);
  if (l?.smooth === true) return occ ? `${occ} CSL` : 'unstated';
  const pax = money(l?.per_passenger);
  if (occ && pax) return `${occ}/${pax}`;
  if (occ) return occ;
  return 'unstated';
}

export function describeMedical(m) {
  const per = money(m?.per_person);
  return per || 'none';
}

// ── THE NAME OF A CORNER ──────────────────────────────────────────────────
//
// A generated corner used to be written with `option_label: null`, and null is
// what every surface renders as «UNLABELLED». Milam's Mach 2 quote carried two
// of them, both PRESENTED, at $8,992 and $9,227 — two priced offers in front of
// a client with no names on them.
//
// NAME ONLY THE AXIS THAT VARIES. Stamping both coordinates onto every corner
// reads as noise when there is nothing to tell apart ("$1M/$100K · $5,000" on a
// quote with one medical limit says the same thing four times). Naming only the
// varying axis is also what makes the name USEFUL: it is precisely the thing
// the broker is choosing between.
//
// AND ONE AXIS IS NOT ALWAYS ENOUGH. Milam's is the case that proves it: the
// liability legs repeat across the medical legs, so corner 3 sits on the SAME
// liability coordinate as corner 1 ($1M/$100K) and is a different price. A name
// built from liability alone would have printed one label on two options, which
// is worse than «UNLABELLED» because it looks decided.
//
// A corner is never named after its premium or its position. Both move.
export function cornerLabel(l, m, { liabilityVaries = true, medicalVaries = true } = {}) {
  const parts = [];
  if (liabilityVaries && !liabilityLegIsBlank(l)) parts.push(describeLiability(l));
  if (medicalVaries && !medicalLegIsBlank(m)) parts.push(`${describeMedical(m)} med`);
  // Nothing varies, or the varying axis carries no stated limit: there is no
  // honest name, so write none rather than a name that says nothing.
  return parts.length ? parts.join(' · ') : null;
}

// The corners, in the order they are generated: liability outer, medical inner,
// so the set reads down the page the way the editor stacks it.
export function buildCorners(liability, medical) {
  const L = (liability || []).length ? liability : [{}];
  const M = (medical || []).length ? medical : [{}];
  const out = [];
  for (const l of L) {
    for (const m of M) {
      out.push({
        liability: l,
        medical: m,
        liability_key: liabilityKey(l),
        medical_key: medicalKey(m),
      });
    }
  }
  return out;
}

// ── READ: IS THIS OPTION SET A GRID, AND WHAT ARE ITS AXES ────────────────
//
// `ok: false` is not a failure, it is an ANSWER: these options differ in more
// than their limits, so they are separate offers and the editor must show them
// one at a time rather than force them into a product. Four of the five live
// multi-option quotes land here and every one of them is right to:
//
//   Global 499ea36f       All Risk / All Risk higher liability / GNIM, and the
//                         GNIM row carries different deductibles. A coverage
//                         configuration is separately rated, never a toggle.
//   Acceleration a411ea71 three AIRCRAFT, hull 45,000 / 70,000 / 120,000
//   Acceleration f8edf033 four fleet configurations, four different hull values
//   Allianz b26d7920      $20M and $25M, and NEITHER records a limit: both rows
//                         carry liability_per_occurrence NULL and say it only in
//                         the label. Two corners on one coordinate is not a grid,
//                         and inventing the limits to make it one would be
//                         writing a number the carrier never stated.
//
// Milam's Mach 2 c82d5c2b is the one that IS a grid, and it derives exactly:
// two liability legs (972, 1267), two medical legs (60, 120), hull 7900.
export function deriveAxes(options) {
  const all = Array.isArray(options) ? options : [];
  // A term variant is a second PRICE for one corner's coverage, not a corner.
  // It inherits its parent's coverage through term_variant_of and is managed by
  // the six-month control, which this editor does not touch.
  const corners = all.filter((o) => !o?.term_variant_of);
  const variants = all.filter((o) => o?.term_variant_of);

  const empty = {
    ok: true, reason: null, liability: [], medical: [], corners: [],
    hull_premium: null, corner_count: 0, variant_count: variants.length,
  };
  if (!corners.length) return empty;

  if (corners.length > 1) {
    for (const f of SHARED_CORNER_FIELDS) {
      const seen = new Set(corners.map((o) => sharedValue(o, f)));
      if (seen.size > 1) {
        return notAGrid(
          `these options do not share ${SHARED_LABEL[f]}, so they are separate offers rather than `
          + 'two points on one grid', variants
        );
      }
    }
  }

  const liabByKey = new Map();
  const medByKey = new Map();
  const cells = new Map();
  for (const o of corners) {
    const lc = liabilityCoord(o);
    const mc = medicalCoord(o);
    const lk = liabilityKey(lc);
    const mk = medicalKey(mc);
    if (!liabByKey.has(lk)) liabByKey.set(lk, { ...lc, key: lk, premiums: [], tria: [], totals: [] });
    if (!medByKey.has(mk)) medByKey.set(mk, { ...mc, key: mk, premiums: [], totals: [] });
    liabByKey.get(lk).premiums.push(axisNum(o.liability_premium));
    liabByKey.get(lk).tria.push(axisNum(o.tria_premium));
    medByKey.get(mk).premiums.push(axisNum(o.medical_premium));
    const cell = `${lk}|${mk}`;
    if (cells.has(cell)) {
      return notAGrid(
        'two of these options carry the same liability and medical payments limits, so the pair '
        + 'differs in something this editor cannot see', variants
      );
    }
    cells.set(cell, o);
  }

  // ORDERED BY THE LIMIT, NOT BY THE ROW ORDER. Supabase returns the options
  // unordered, so "the first leg" off insertion order is whatever the storage
  // engine last did to the heap — the defect that put David's save on the wrong
  // Mach 2 option on 2026-08-06, one layer up. The limit is a property of the
  // AXIS and sorts the same on every read, and it also reads the way the page
  // stacks it: $1M above $2M, $5,000 med above $10,000.
  const byLimit = (a, b, f) => {
    const av = a[f]; const bv = b[f];
    if (av == null && bv == null) return 0;
    if (av == null) return -1;
    if (bv == null) return 1;
    return av - bv;
  };
  const L = [...liabByKey.values()].sort((a, b) =>
    byLimit(a, b, 'per_occurrence') || byLimit(a, b, 'per_passenger') || a.key.localeCompare(b.key));
  const M = [...medByKey.values()].sort((a, b) =>
    byLimit(a, b, 'per_person') || a.key.localeCompare(b.key));
  if (L.length * M.length !== corners.length) {
    return notAGrid(
      `these ${corners.length} options are not every combination of their `
      + `${L.length} liability and ${M.length} medical payments limits, so switching one limit `
      + 'would reach a price nobody quoted', variants
    );
  }

  const hullPremium = axisNum(corners[0].hull_premium);

  // ONE PREMIUM PER LEG, AND IT MUST NOT BE INVENTED. A leg's recorded premium is
  // whatever its corners agree on; corners that recorded nothing are silent
  // rather than zero. Milam's Mach 2 records medical_premium 60 on ONE of the two
  // corners that carry the $5,000 limit and nothing on the other, and the answer
  // for that leg is 60.
  //
  // Where nothing is recorded, the stored totals may still IMPLY a figure
  // (total - hull - liability). That is offered as a chip and never written: it
  // is only meaningful when every corner on the leg implies the SAME number, and
  // a residual off a sum that forgot a column measures the omission.
  const legPremium = (vals) => {
    const set = [...new Set(vals.filter((v) => v != null))];
    return set.length === 1 ? set[0] : null;
  };

  const liability = L.map((l) => ({
    per_occurrence: l.per_occurrence,
    per_passenger: l.per_passenger,
    smooth: l.smooth,
    key: l.key,
    premium: legPremium(l.premiums),
    // TRIA rides the liability leg. It is a per-option column and always has
    // been; what the grid forced was a statement of WHICH axis it travels with,
    // and the book answers unambiguously: Allianz priced 675 against $20M and
    // 840 against $25M, and Milam's four Mach 2 corners all carry 275, one per
    // liability leg twice over.
    tria_premium: legPremium(l.tria),
  }));
  const medical = M.map((m) => ({
    per_person: m.per_person,
    key: m.key,
    premium: legPremium(m.premiums),
  }));

  // The residual each leg's corners imply, only when they all imply the same
  // thing. Liability first (hull is known), then medical against the resolved
  // liability legs.
  for (const l of liability) {
    if (l.premium != null || liabilityLegIsBlank(l) || hullPremium == null) continue;
    l.premium_implied = impliedFrom(corners, hullPremium, (o) => liabilityKey(liabilityCoord(o)) === l.key,
      (o) => axisNum(medByKey.get(medicalKey(medicalCoord(o)))?.premiums.find((v) => v != null) ?? 0));
  }
  for (const m of medical) {
    if (m.premium != null || medicalLegIsBlank(m) || hullPremium == null) continue;
    m.premium_implied = impliedFrom(corners, hullPremium, (o) => medicalKey(medicalCoord(o)) === m.key,
      (o) => {
        const leg = liability.find((l) => l.key === liabilityKey(liabilityCoord(o)));
        return leg ? (leg.premium ?? leg.premium_implied ?? null) : null;
      });
  }

  return {
    ok: true,
    reason: null,
    liability,
    medical,
    hull_premium: hullPremium,
    corner_count: corners.length,
    variant_count: variants.length,
    corners: corners.map((o) => ({
      option_id: o.id,
      liability_key: liabilityKey(liabilityCoord(o)),
      medical_key: medicalKey(medicalCoord(o)),
      total_premium: axisNum(o.total_premium),
      presented: o.presented === true,
      selected: o.selected === true,
      option_label: o.option_label || null,
    })),
  };
}

// total - hull - (the other axis's premium), agreed across every corner on this
// leg or not offered at all. `otherLeg` returning null means the other axis is
// itself unknown, so nothing can be implied.
function impliedFrom(corners, hullPremium, onThisLeg, otherLeg) {
  const vals = [];
  for (const o of corners) {
    if (!onThisLeg(o)) continue;
    const total = axisNum(o.total_premium);
    const other = otherLeg(o);
    if (total == null || other == null) return undefined;
    vals.push(total - hullPremium - other);
  }
  const set = [...new Set(vals)];
  return set.length === 1 && set[0] >= 0 ? set[0] : undefined;
}

function notAGrid(reason, variants) {
  return {
    ok: false, reason, liability: [], medical: [], corners: [],
    hull_premium: null, corner_count: 0, variant_count: (variants || []).length,
  };
}

// ── DID THE AXES ACTUALLY CHANGE ──────────────────────────────────────────
// Every step of this wizard sends every field it holds, which is how a sheet
// left open across an outside write restores its own stale copy (it ate a $60
// med-pay premium twice on 2026-08-07). The corner set is the most destructive
// thing on the page — a stale write DELETES rows — so the axes are sent only
// when they differ from what was loaded.
export function axesSignature(liability, medical, hullPremium) {
  const l = (liability || []).map((x) => [
    liabilityKey(x), axisNum(x.premium), axisNum(x.tria_premium),
  ].join(':')).join('|');
  const m = (medical || []).map((x) => [medicalKey(x), axisNum(x.premium)].join(':')).join('|');
  return `${axisNum(hullPremium)}#${l}#${m}`;
}
